import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const ContactFormModal = ({ isOpen, onClose, language = 'en' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const recaptchaRef = useRef(null);
  const modalRef = useRef(null);
  const messageRef = useRef(null);

  const texts = {
    en: {
      title: 'Contact Sales',
      subtitle: 'Fill out the form and our team will get back to you within 24 hours.',
      name: 'Full Name',
      email: 'Email Address',
      company: 'Company Name',
      phone: 'Phone Number',
      message: 'Message',
      messagePlaceholder: 'Tell us about your payment processing needs...',
      submit: 'Send Message',
      submitting: 'Sending...',
      success: 'Message sent successfully! We\'ll contact you soon.',
      error: 'Failed to send message. Please try again.',
      required: 'Required',
      captchaError: 'Please complete the captcha verification',
      close: 'Close'
    },
    es: {
      title: 'Contactar Ventas',
      subtitle: 'Completa el formulario y nuestro equipo te contactará en 24 horas.',
      name: 'Nombre Completo',
      email: 'Correo Electrónico',
      company: 'Nombre de la Empresa',
      phone: 'Número de Teléfono',
      message: 'Mensaje',
      messagePlaceholder: 'Cuéntanos sobre tus necesidades de procesamiento de pagos...',
      submit: 'Enviar Mensaje',
      submitting: 'Enviando...',
      success: '¡Mensaje enviado exitosamente! Te contactaremos pronto.',
      error: 'Error al enviar el mensaje. Por favor intenta de nuevo.',
      required: 'Requerido',
      captchaError: 'Por favor completa la verificación captcha',
      close: 'Cerrar'
    }
  };

  const t = texts[language] || texts.en;

  // Load reCAPTCHA script
  useEffect(() => {
    if (isOpen && !window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
      script.async = true;
      script.defer = true;
      
      window.onRecaptchaLoad = () => {
        setCaptchaLoaded(true);
      };
      
      document.head.appendChild(script);
      
      return () => {
        // Cleanup
        delete window.onRecaptchaLoad;
      };
    } else if (window.grecaptcha) {
      setCaptchaLoaded(true);
    }
  }, [isOpen]);

  // Render reCAPTCHA when loaded
  useEffect(() => {
    if (captchaLoaded && isOpen && recaptchaRef.current && window.grecaptcha) {
      // Clear any existing captcha
      recaptchaRef.current.innerHTML = '';
      
      try {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => {
            setCaptchaToken(token);
          },
          'expired-callback': () => {
            setCaptchaToken(null);
          }
        });
      } catch (e) {
        // Already rendered
      }
    }
  }, [captchaLoaded, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
      setSubmitStatus(null);
      setCaptchaToken(null);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaToken) {
      setSubmitStatus('captcha-error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaToken
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
        // Reset captcha
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
        setCaptchaToken(null);
        // Scroll to show success message
        setTimeout(() => {
          if (messageRef.current) {
            messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        setSubmitStatus('error');
        // Scroll to show error message
        setTimeout(() => {
          if (messageRef.current) {
            messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label={t.close}
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">{t.title}</h2>
          <p className="text-blue-100 mt-1">{t.subtitle}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.name} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              data-testid="contact-name-input"
            />
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.email} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              data-testid="contact-email-input"
            />
          </div>

          {/* Company Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.company}
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              data-testid="contact-company-input"
            />
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.phone}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              data-testid="contact-phone-input"
            />
          </div>

          {/* Message Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.message} <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              placeholder={t.messagePlaceholder}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
              data-testid="contact-message-input"
            />
          </div>

          {/* reCAPTCHA */}
          <div className="flex justify-center">
            <div ref={recaptchaRef} data-testid="recaptcha-container"></div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 text-lg font-semibold"
            data-testid="contact-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {t.submit}
              </>
            )}
          </Button>

          {/* Success/Error Messages */}
          <div ref={messageRef}>
            {submitStatus === 'success' && (
              <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>{t.success}</span>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{t.error}</span>
              </div>
            )}

            {submitStatus === 'captcha-error' && (
              <div className="flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{t.captchaError}</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactFormModal;
